import { ITutorRequest } from '@wot/typings';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import tutorRequestsApi from '../../../../api/tutor-requests';
import { ARROWDOWN_ICON } from '../../../../assets/icons';
import { DropDownItem, Dropdown, MenuProps } from '../../../../components/DropDown';
import ModalContainer, { ConfirmationBox } from '../../../../components/Modal';
import { TableRowItemData } from '../../../../components/Table';
import { useQueryClient } from '@tanstack/react-query';

export function TableRowItem({ tutor, setTutor }: { tutor: ITutorRequest, setTutor?: any }) {
  const [statusOptions, setStatusOptions] = useState<MenuProps[]>([]);
  const queryClient = useQueryClient();

  const sendToRevision = () => {
    if (
      tutor.personal.status === 'issue' ||
      tutor.education.status === 'issue' ||
      tutor.experience.status === 'issue' ||
      tutor.profession.status === 'issue'
    ) {
      tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          status: 'revision'
        }
      }).then(() => {
        setTutor({ ...tutor, status: 'revision' })
        queryClient.invalidateQueries({ queryKey: ['request-tutors'] });
        toast.success('Tutor sent to revision');
      })
    } else {
      toast.error('No Issues Found');
    }
  };

  const sendToGenerteAgreement = () => {
    if (
      tutor.personal.status === 'verified' &&
      tutor.education.status === 'verified' &&
      tutor.experience.status === 'verified' &&
      tutor.profession.status === 'verified'
    ) {
      tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          status: 'gen-agreement'
        }
      }).then(() => {
        setTutor && setTutor({ ...tutor, status: 'gen-agreement' })
        queryClient.invalidateQueries({ queryKey: ['request-tutors'] });
        toast.success('Tutor sent to generate agreement');
      })
    } else {
      toast.error('Profile is not Verified');
    }
  };

  const sendToTutorDescision = () => {
    tutorRequestsApi.updateStatus({
      _id: tutor._id,
      data: {
        status: 'rev-agreement'
      }
    }).then(() => {
      setTutor && setTutor({ ...tutor, status: 'rev-agreement' })
      queryClient.invalidateQueries({ queryKey: ['request-tutors'] });
      toast.success('Tutor sent to review agreement');
    })
  };

  const sendToRejected = () => {
    tutorRequestsApi.updateStatus({
      _id: tutor._id,
      data: {
        status: 'rejected'
      }
    }).then(() => {
      setTutor && setTutor({ ...tutor, status: 'rejected' })
      queryClient.invalidateQueries({ queryKey: ['request-tutors'] });
      toast.success('Tutor rejected');
    })
  };

  useEffect(() => {
    let items: MenuProps[] = [];
    if (tutor.status === 'under-review') {
      items = [
        {
          key: 1,
          label: (
            <ModalContainer
              ModalComp={<ConfirmationBox text="Confirm Move to Revision?" onConfirm={() => sendToRevision()} />}
            >
              <DropDownItem label="Revision" />
            </ModalContainer>
          )
        },
        {
          key: 2,
          label: (
            <ModalContainer
              ModalComp={
                <ConfirmationBox
                  text="Confirm Move to Generate Agreement?"
                  onConfirm={() => sendToGenerteAgreement()}
                />
              }
            >
              <DropDownItem label="Generate Agreement" />
            </ModalContainer>
          )
        },
        {
          key: 3,
          label: (
            <ModalContainer
              ModalComp={<ConfirmationBox text="Confirm Move to Rejected?" onConfirm={() => sendToRejected()} />}
            >
              <DropDownItem label="Reject" />
            </ModalContainer>
          )
        }
      ];
      setStatusOptions(items);
      return;
    }

    if (tutor.status === 'gen-agreement') {
      items = [
        {
          key: 1,
          label: (
            <ModalContainer
              ModalComp={
                <ConfirmationBox text="Confirm Move to Tutor Decision?" onConfirm={() => sendToTutorDescision()} />
              }
            >
              <DropDownItem label="Tutor Decision" />
            </ModalContainer>
          )
        },
        {
          key: 2,
          label: (
            <ModalContainer
              ModalComp={<ConfirmationBox text="Confirm Move to Rejected?" onConfirm={() => sendToRejected()} />}
            >
              <DropDownItem label="Reject" />
            </ModalContainer>
          )
        }
      ];
      setStatusOptions(items);
      return;
    }

    if (tutor.status === 'revision') {
      items = [
        {
          key: 1,
          label: (
            <ModalContainer
              ModalComp={
                <ConfirmationBox
                  text="Confirm Move to Generate Agreement?"
                  onConfirm={() => sendToGenerteAgreement()}
                />
              }
            >
              <DropDownItem label="Generate Agreement" />
            </ModalContainer>
          )
        },
        {
          key: 2,
          label: (
            <ModalContainer
              ModalComp={<ConfirmationBox text="Confirm Move to Rejected?" onConfirm={() => sendToRejected()} />}
            >
              <DropDownItem label="Reject" />
            </ModalContainer>
          )
        }
      ];
    }

    if (tutor.status === 'rev-agreement') {
      items = [
        {
          key: 1,
          label: (
            <ModalContainer
              ModalComp={<ConfirmationBox text="Confirm Move to Rejected?" onConfirm={() => sendToRejected()} />}
            >
              <DropDownItem label="Reject" />
            </ModalContainer>
          )
        }
      ];
      setStatusOptions(items);
    }
  }, [tutor]);

  return (
    <>
      <TableRowItemData text={format(parseISO(tutor.createdAt), 'dd/MM/yyyy')} />
      <TableRowItemData text={tutor._id} />
      <TableRowItemData text={tutor?.personal?.data.name || ''} />
      <TableRowItemData text={tutor?.personal?.data.email || ''} />
      <TableRowItemData text={tutor?.personal?.data.phoneNumber || ''} />
      <TableRowItemData text={tutor?.profession?.data.categoryType || ''} />
      <TableRowItemData
        component={
          <div className="w-max flex items-center justify-between">
            {tutor.status === 'pending' || tutor.status === 'revision' ? (
              <div
                className={`bg-blue-200 text-blue-700 w-full flex items-center justify-between gap-4 px-4 py-1 rounded-full cursor-pointer`}
              >
                <p className="text-xs font-semibold">{tutor.status}</p>
              </div>
            ) : (
              <Dropdown menu={statusOptions} placement="bottomLeft">
                <div
                  className={`bg-[#FFE1C9] text-[#BA5300] w-full flex items-center justify-between gap-4 px-4 py-1 rounded-full cursor-pointer`}
                >
                  <p className="text-xs font-semibold">{tutor.status}</p>
                  <img src={ARROWDOWN_ICON} className={`w-4`} />
                </div>
              </Dropdown>
            )}
          </div>
        }
      />
    </>
  );
}

// ${status === "under-review" && "bg-[#FFE1C9] text-[#BA5300]"}
// ${status === "revision" && "bg-[#DAFDFF] text-[#006B72] "}
// ${status === "gen-agreement" && "bg-[#E9EDF5] text-[#5A6376]"}
// ${status === "rev-agreement" && "bg-[#EEFCE0] text-[#498839]"}
// ${status === "rejected" && "bg-[#EEFCE0] text-[#498839]"}
